import CustomHtmlKeyboardResponsePlugin from "../../customPlugin/customHtmlKeyboardResponse";

import { KEY_CODES } from "../../gearshiftGame/constants";
import { Game } from "../../gearshiftGame/game";
import { Score } from "../../gearshiftGame/score";
import { createStimulus, hideElement } from "../../gearshiftGame/utils";
import {
  getGearshiftMissionConfig,
  getLayoutVideoUrl,
  resolveMediaUrl,
  resolveLayoutPlaceholders,
} from "../../lib/utils";
import { buildFeedbackTrial } from "../trials/feedback";
import { buildInstructionsTrial } from "../trials/instructions";
import { updateDebugMissionConfig } from "../ui/debugHelpers";
import { buildAchievementsTrial } from "../trials/achievements";
import { createInstructionVideoOverlay } from "../ui/instructionVideo";
import { tutorialToggles } from "../../config/tutorialToggles";
import { addFirstTaskMetadata } from "./utils/missionHelpers";
import { buildTutorialWithPhasesMissionFive } from "./utils/buildTutorialWithPhasesMissionFive";
import {
  buildPhase0,
  buildPhase0_1_M5,
  buildPhase0_2_M5,
  buildPhase0_3_M5,
  buildPhase0_4_M5,
  buildPhase0_4B_M5,
  buildPhase0_5_M5,
  buildPhase0_6_M5,
  buildPhase7,
} from "./phases/missionFivePhases";
import { buildSkipMissionProcedure } from "./skipMission";
import {
  MISSION_FIVE_PAGE_COUNT,
  buildMissionFiveInstructionPages,
} from "./instructionBuilders/missionFive/buildMissionFiveInstructionPages";
import { injectSurveyPanel, removeSurveyPanel } from "./hq/buildHQSurveyPanel.js";
import { injectHQMessage, removeHQMessage } from "./hq/buildHQMessage.js";
import { injectHQLaneChoiceMessage, removeHQLaneChoiceMessage } from "./hq/buildHQLaneChoiceMessage.js";
import { getHQMessagePanelConfig } from "./hq/localHQMessages.js";

/**
 * @type {number}
 * @description The current mission
 */
const MISSION_NUMBER = 5;

/**
 * @type {Score}
 * @description The user's score
 * NOTE: Used here so we don't have to pass it through every single function
 */
let SCORE;

/**
 * Global counter for tutorial failures in this mission
 */
let tutorialFailureCount = 0;

/**
 * Global failure limit for this mission
 */
let failureLimit = 2;

function getLocalHQConfigFromAgentFile(agentFile) {
  if (!agentFile) return null;
  return getHQMessagePanelConfig(MISSION_NUMBER, agentFile);
}

function removeAllHQPanels() {
  removeHQMessage();
  removeHQLaneChoiceMessage();
}

function maybeInjectHQPanel(jsPsych, game, agentFile, options = {}) {
  const hqConfig = getLocalHQConfigFromAgentFile(agentFile);
  if (!hqConfig) return false;
  if (options.laneChoiceOnly && hqConfig.variant !== "laneChoice") return false;

  if (game?.agentImage) {
    hideElement(game.agentImage);
  }

  if (hqConfig.variant === "laneChoice") {
    injectHQLaneChoiceMessage(jsPsych, hqConfig);
  } else {
    injectHQMessage(jsPsych, hqConfig);
  }
  return true;
}

function getIndexedAgentFile(agentFile, index) {
  if (Array.isArray(agentFile)) return agentFile[index] ?? agentFile[0] ?? null;
  return agentFile ?? null;
}

/**
 * Builds the first mission of the game
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @returns The timeline object for the first mission
 */
export async function buildMissionFive(jsPsych, metadata) {
  // Reset tutorial failure counter when entering this mission
  tutorialFailureCount = 0;

  // Check if a config file was given for this mission
  let config;
  try {
    config = await getGearshiftMissionConfig(jsPsych, metadata, MISSION_NUMBER);
  } catch (e) {
    console.error(`Unable to load mission config file for mission ${MISSION_NUMBER}`);
  }

  // No config file was given for mission 1, skip this mission (no file given or failed to load the file)
  if (!config) return buildSkipMissionProcedure(MISSION_NUMBER);

  // Config file given - build the mission!
  console.log("Mission Five Configuration", config);

  // Update debug overlay with mission configuration
  updateDebugMissionConfig(jsPsych, config);

  // Initialize variables shared across the mission
  const {
    missionLives,
    missionMinScore,
    missionMaxCrash,
    missionNumTrials,
    instructionsMaxRetries,
    instructionsAudio,
    trials,
    thisFile,
  } =
    config;
  const missionTutorials = {
    ...(tutorialToggles.mission5 ?? {}),
    ...(config.tutorials ?? {}),
    ...(instructionsAudio !== undefined ? { instructionsAudio } : {}),
  };
  failureLimit = instructionsMaxRetries ?? missionTutorials.failsCountLimit ?? 2;
  SCORE = new Score(
    jsPsych,
    missionLives, // # of lives to start the mission with
    metadata.rewardReductionByReset // Amount to reduce score by when the user runs out of lives
  );

  // Build the timeline from the config's trials array
  const missionFiveTimeline = trials.map((trialConfig) => {
    const stimulus = () => createStimulus(MISSION_NUMBER, missionNumTrials, trialConfig);
    switch (trialConfig.trialType) {
      case "INSTRUCTION":
        // NOTE: Instructions trial is the first trial of the mission
        return buildMissionFiveInstructions(jsPsych, trialConfig, metadata, stimulus(), trials, missionTutorials);
      case "TASK":
        return buildMissionFiveTask(jsPsych, metadata, trialConfig, stimulus);
      case "MESSAGE":
        return buildMessageTrial(jsPsych, metadata, trialConfig, stimulus);
      case "SURVEY":
        return buildMissionFiveSurvey(jsPsych, metadata, trialConfig, stimulus);
      case "FEEDBACK":
        // NOTE: Feed back trial is the last trial of the mission
        return buildMissionFiveFeedbackTask(jsPsych, trialConfig, missionMinScore, missionMaxCrash);
      default:
        throw new Error("An unknown trial type was found:", trialConfig.trialType);
    }
  });

  // Add task_metadata to the first TASK trial
  addFirstTaskMetadata(missionFiveTimeline, trials, metadata);

  return {
    data: {
      missionNumber: MISSION_NUMBER,
      missionFile: thisFile,
      missionNumTrials,
      skipped: false,
    },
    save_trial_parameters: { stimulus: false },
    timeline: missionFiveTimeline,
  };
}

/**
 * Builds the trial for displaying the instructions for mission five
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The configuration object for a specific trial
 * @param {Object} metadata The global metadata of the experiment
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} allTrials All trials from the mission config
 * @param {Object} missionTutorials Tutorial toggles for this mission
 * @returns
 */
function buildMissionFiveInstructions(
  jsPsych,
  trialConfig,
  metadata,
  stimulus,
  allTrials,
  missionTutorials = {}
) {
  // Get audio enabled status from backend config, fallback to local configs
  const instructionAudioEnabled = missionTutorials.instructionsAudio ?? tutorialToggles.mission5?.instructionsAudio ?? true;
  const instructionAudio = (src) => (instructionAudioEnabled ? src : undefined);
  const instructionVideoEnabled = missionTutorials.instructionVideoSlide ?? true;
  const missionFiveHeadquarterAssets = [
    "HQmessageM5m1",
    "HQmessageM5m2",
    "HQmessageM5m3",
  ];

  // Fixed tutorial trial config — ensures the tutorial always uses the same
  // known-good trial regardless of what the loaded config file contains.
  const tutorialConfig = {
    CSI: 100,
    agentFile: missionFiveHeadquarterAssets,
    avoidTruck: 1,
    block: 1,
    carLeft: "gang_medium.svg",
    carLeftLane: 2,
    carLeftNumber: 3,
    carLeftNumberL: 8,
    carLeftNumberR: 8,
    carLeftPackageL: "package_medium.svg",
    carLeftPackageR: "package_medium.svg",
    carRight: "gang_medium.svg",
    carRightLane: 6,
    carRightNumber: 6,
    carRightNumberL: 5,
    carRightNumberR: 5,
    carRightPackageL: "package_low.svg",
    carRightPackageR: "package_low.svg",
    gameType: "SH",
    gangSpeed: 5,
    package_appearance: 150,
    signalUncertainty: "low",
    trialNumber: 1,
    trialType: "TASK",
    truckFollowProposal: 1,
    truckImage: "truck_round_orange_happy.svg",
    truckLane: 3,
    truckSpeed: 5,
    truckTrustsignal: "",
    truckType: "kind",
  };
  const tutorialStimulus = createStimulus(MISSION_NUMBER, 1, tutorialConfig);

  const pages = buildMissionFiveInstructionPages(jsPsych, instructionAudio);
  const totalPages = MISSION_FIVE_PAGE_COUNT;

  const timeline = [];
  const tutorialEnabled = (key, fallback = true) => missionTutorials[key] ?? fallback;

  // Pages 1-5
  const firstPages = pages.slice(0, Math.min(5, pages.length));
  if (firstPages.length) {
    timeline.push(
      buildInstructionsTrial(jsPsych, firstPages, {
        total_pages: totalPages,
        page_offset: 0,
      })
    );
  }

  // Practice after 5.5:
  // user must choose solo chase (Z), then select lane and finish without crashing.
  if (pages.length >= 5 && tutorialEnabled("soloFlow", true)) {
    timeline.push(
      buildMissionFiveTaskWithPhasesCustom(
        jsPsych,
        metadata,
        tutorialConfig,
        tutorialStimulus,
        [0, "0.1", "0.3", "0.5", "0.6"],
        {
          tutorialHeader: {
            title: "Practicing solo chase (1/1)",
            instruction: "Press '__BACKWARD_KEY__' to chase alone, then choose your lane safely",
          },
          requiredCooperationChoice: "solo",
          ready: {
            imageOrVideoUrl: () => getLayoutVideoUrl("instructions/videos/m5/mission_5-05.mp4"),
          },
        }
      )
    );
  }

  // Pages 6-7
  if (pages.length > 5) {
    timeline.push(
      buildInstructionsTrial(jsPsych, pages.slice(5, 7), {
        total_pages: totalPages,
        page_offset: 5,
      })
    );
  }

  // Practice after 5.7:
  // user must propose lane for partner, then choose lane for themselves.
  if (pages.length >= 7 && tutorialEnabled("cooperationFlow", true)) {
    timeline.push(
      buildMissionFiveTaskWithPhasesCustom(
        jsPsych,
        metadata,
        tutorialConfig,
        tutorialStimulus,
        [0, "0.1", "0.2", "0.4", "0.4b", "0.3", "0.5", "0.6"],
        {
          tutorialHeader: {
            title: "Practicing partner proposal (1/1)",
            instruction: "Press '__FORWARD_KEY__', propose partner lane, then choose your own lane",
          },
          requiredCooperationChoice: "cooperate",
          ready: {
            imageOrVideoUrl: () => getLayoutVideoUrl("instructions/videos/m5/mission_5-07.mp4"),
          },
        }
      )
    );
  }

  // Pages 8-9
  if (pages.length > 7) {
    timeline.push(
      buildInstructionsTrial(jsPsych, pages.slice(7, 9), {
        total_pages: totalPages,
        page_offset: 7,
      })
    );
  }

  // Video slide after 5.9
  if (instructionVideoEnabled && pages.length >= 9) {
    timeline.push(buildMissionFiveInstructionVideoSlide(jsPsych, () => getLayoutVideoUrl("instructions/videos/m5/mission_5-09.mp4")));
  }

  // Page 10 (5.10)
  if (pages.length > 9) {
    timeline.push(
      buildInstructionsTrial(jsPsych, pages.slice(9, 10), {
        total_pages: totalPages,
        page_offset: 9,
      })
    );
  }

  return {
    data: { trialConfig },
    timeline,
    on_finish: (data) => SCORE.assignScore(data),
  };
}

function buildMissionFiveInstructionVideoSlide(jsPsych, videoUrl) {
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus: "",
    choices: "NO_KEYS",
    data: { name: "instructionsVideoOnly" },
    on_load: () => {
      const resolvedVideoUrl = resolveMediaUrl(videoUrl);
      const overlay = createInstructionVideoOverlay({
        videoUrl: resolvedVideoUrl,
        commentImageUrl: "assets/images/instructions/custom/extras/m5/videoSlideCommentM5.svg",
        commentImageTop: "80%",
        commentImageLeft: "35%",
        commentImageWidth: "min(60vw, 800px)",
        commentImageHeight: "auto",
        containerVerticalOffset: "-70px",
        backgroundImageUrl: "assets/images/instructions/custom/extras/m5/backgroundM5.svg",
        onContinue: () => {
          jsPsych.finishTrial({ name: "instructionsVideoOnly", videoUrl: resolvedVideoUrl });
        },
        forwardText: resolveLayoutPlaceholders(jsPsych, '[press "__FORWARD_KEY__" twice to continue ] ->'),
        backText: resolveLayoutPlaceholders(jsPsych, '[press "__REPLAY_KEY__" to replay]'),
      });
      document.body.appendChild(overlay);
    },
    on_finish: () => {
      const overlay = document.getElementById("gear-instruction-video-overlay");
      if (!overlay) return;
      if (typeof overlay.__cleanup === "function") {
        overlay.__cleanup();
      } else if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
    },
  };
}

/**
 * Builds a tutorial with selected phases for Mission 5.
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Array} phases Array of phase numbers to include in the tutorial
 * @param {Object} options Optional overrides: agentFile, disableAcceleration, disableDeceleration, hideTargetCar, customTrialConfigOverrides, ready/congrats overlays
 * @returns A jsPsych node with the tutorial phases
 */
function buildMissionFiveTaskWithPhasesCustom(
  jsPsych,
  metadata,
  trialConfig,
  stimulus,
  phases = [0, "0.1", "0.2", "0.4", "0.4b", "0.3", "0.5", "0.6"],
  options = {}
) {
  const buildersMap = {
    0: buildPhase0,
    "0.1": buildPhase0_1_M5,
    "0.2": buildPhase0_2_M5,
    "0.3": buildPhase0_3_M5,
    "0.4": buildPhase0_4_M5,
    "0.4b": buildPhase0_4B_M5,
    "0.5": buildPhase0_5_M5,
    "0.6": buildPhase0_6_M5,
    7: buildPhase7,
  };

  const { agentFile, tutorialHeader, ...customOptions } = options;

  let finalStimulus = stimulus;
  if (tutorialHeader) {
    const configForStimulus = {
      ...trialConfig,
      ...(agentFile ? { agentFile } : {}),
      tutorialHeader,
    };
    finalStimulus = createStimulus(MISSION_NUMBER, 1, configForStimulus);
  }

  const defaultOverlayOptions = {
    ready: {
      forwardText: `[press "__FORWARD_KEY__" twice to start]`,
      backText: `[press "__REPLAY_KEY__" to play video]`,
      imageOrVideoUrl: "assets/images/instructions/video_placeholder.svg",
      showText: false,
    },
    congrats: {
      forwardText: `[press "__FORWARD_KEY__" to continue ] ->`,
      backText: "",
      imageOrVideoUrl: "assets/images/instructions/demo_congratulations.png",
      forwardTextCrash: `[press "__FORWARD_KEY__" to try again ]`,
      backTextCrash: "",
      imageOrVideoUrlCrash: "assets/images/instructions/demo_try_again.png",
      showText: false,
    },
  };

  const overlayOptions = {
    ready: { ...defaultOverlayOptions.ready, ...customOptions.ready },
    congrats: { ...defaultOverlayOptions.congrats, ...customOptions.congrats },
    followUpOnly: customOptions.followUpOnly ?? false,
  };

  const trialConfigWithOverrides = {
    ...trialConfig,
    ...(agentFile ? { agentFile } : {}),
    ...(customOptions.disableAcceleration !== undefined
      ? { disableAcceleration: customOptions.disableAcceleration }
      : {}),
    ...(customOptions.disableDeceleration !== undefined
      ? { disableDeceleration: customOptions.disableDeceleration }
      : {}),
    ...(customOptions.hideTargetCar !== undefined
      ? { hideTargetCar: customOptions.hideTargetCar }
      : {}),
    ...(customOptions.customTrialConfigOverrides || {}),
  };

  const phasePartnerAgentFile = getIndexedAgentFile(trialConfigWithOverrides.agentFile, 1);
  const phaseDriverAgentFile = getIndexedAgentFile(trialConfigWithOverrides.agentFile, 2);
  const phaseCooperationAgentFile = getIndexedAgentFile(trialConfigWithOverrides.agentFile, 0);
  const phaseAgentFileByPhase = {
    "0.1": phaseCooperationAgentFile,
    "0.4": phasePartnerAgentFile,
    "0.3": phaseDriverAgentFile,
  };
  const laneChoicePhases = new Set(["0.4", "0.3"]);

  return buildTutorialWithPhasesMissionFive({
    jsPsych,
    metadata,
    score: SCORE,
    trialConfig: trialConfigWithOverrides,
    stimulus: finalStimulus,
    phases,
    buildersMap,
    overlayOptions,
    customOptions: {
      addPhase2Delay: customOptions.addPhase2Delay ?? false,
      requiredCooperationChoice: customOptions.requiredCooperationChoice ?? null,
    },
    disableAcceleration: customOptions.disableAcceleration ?? false,
    hooks: {
      onPhaseStart: (phase, game) => {
        const agentFile = phaseAgentFileByPhase[phase];
        if (!agentFile) return;
        maybeInjectHQPanel(jsPsych, game, agentFile, {
          laneChoiceOnly: laneChoicePhases.has(phase),
        });
      },
      onPhaseFinish: (phase) => {
        if (phase === "0.1" || phase === "0.4" || phase === "0.3") {
          removeAllHQPanels();
        }
      },
      onBeforeCongratsOverlay: ({ tryAgain }) => {
        // Ensure no HQ panel leaks into the preserved/frozen frame behind overlays.
        removeAllHQPanels();

        if (!tryAgain) return false;

        tutorialFailureCount += 1;
        console.log(
          `❌ Tutorial failed (attempt ${tutorialFailureCount}/${failureLimit})`
        );

        if (tutorialFailureCount >= failureLimit) {
          window.location.href = "supportPage.html";
          return true;
        }

        return false;
      },
    },
  });
}

/**
 * Builds the trial needed for mission five's task
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
function buildMissionFiveTask(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);

  const moveTruckPhase = {
    timeline: [
      //conditional phase where cooperate == 1
      //move truck from left lane to center lane
      {
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase0_2M5(),
        on_finish: (data) => {
          data.name = "moveTruckCenter";
          SCORE.assignScore(data);
        },
      },
    ],
    conditional_function: () => {
      return newGame.cooperating === 1;
    },
  };

  const choosePartnerLanePhase = {
    timeline: [
      //conditional phase cooperate == true
      //propose lane partner shows up in license window
      //now user chooses a lane for its partner.
      {
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        on_load: () => {
          newGame.startPhase0_4M5();
          maybeInjectHQPanel(jsPsych, newGame, getIndexedAgentFile(trialConfig.agentFile, 1), {
            laneChoiceOnly: true,
          });
        },
        on_finish: (data) => {
          removeAllHQPanels();
          data.name = "proposePartnerLane";
          data.partnerKeyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null;
          SCORE.assignScore(data);
        },
      },
      {
        choices: "NO_KEYS",
        trial_duration: 1000,
        on_load: () => {
          const lastTrialData = jsPsych.data.get().last(1).values()[0];
          const partnerKeyCode = lastTrialData.partnerKeyCode;
          newGame.startPhase0_4BM5(partnerKeyCode);
        },
      },
    ],
    conditional_function: () => {
      return newGame.cooperating === 1;
    },
  };

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    timeline: [
      //Phase 0: Board initializes, end of phase 0 is when target car appears
      {
        choices: "NO_KEYS",
        trial_duration: newGame.targetLaunchDelay,
        on_load: () => {
          newGame.startPhase0();
        },
        on_finish: (data) => {
          data.name = "initializeTarget";
          data.driverSpeed = newGame.driverCarSpeed;
          data.distanceToTarget = newGame.driverDistanceToTarget;
          data.trialStart = {
            name: "trialStart",
            driverSpeed: newGame.driverCarSpeed,
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
          };
          SCORE.assignScore(data);
        },
      },

      //Show two target cars, truck, driver
      //user will decide whether to cooperate or not
      //User chooses either to cooperate or not, by pressing either "z" or "/"
      {
        choices: ["KeyZ", "Slash"],
        on_load: () => {
          newGame.startPhase0_1M5();
          maybeInjectHQPanel(jsPsych, newGame, getIndexedAgentFile(trialConfig.agentFile, 0));
          console.log(
            `[Phase0.1M5] cooperate dialog mounted at performance.now()=${performance.now().toFixed(2)}`
          );
          // Fired only after the lane labels, gang cars/truck, and dialog above are all
          // mounted, so TTL lines up with everything appearing together in the footer
          newGame.firePhase0_1M5TTL();
        },
        on_finish: (data) => {
          removeAllHQPanels();
          data.name = "choiceCooperation";
          data.pulseTTLtrialStart = newGame.ttlPulseFired ? 1 : 0;
          data.TTLpulse_time = newGame.ttlPulseTime;
          if ((data.response_event_code ?? data.response) === "Slash") {
            // "/" choice means cooperate
            newGame.cooperating = 1;
          } else {
            newGame.cooperating = 0;
          }
          SCORE.assignScore(data);
        },
      },

      moveTruckPhase,

      //conditional, only happens if user chooses to cooperate
      choosePartnerLanePhase,

      //for both phases where cooperate == true or cooperate == false
      //values will be shown on left/right of each target car
      //user chooses lane for driver
      {
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        on_load: () => {
          newGame.startPhase0_3M5();
          maybeInjectHQPanel(jsPsych, newGame, getIndexedAgentFile(trialConfig.agentFile, 2), {
            laneChoiceOnly: true,
          });
        },
        on_finish: (data) => {
          removeAllHQPanels();
          data.name = "choiceLaneDriver";
          data.userKeyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null; // keyCode is null if no response is given
          SCORE.assignScore(data);
        },
      },

      //for both phases where cooperate == true or cooperate == false
      //for cooperate == true
      //now that two lanes are chosen, 1 for driver and 1 for partner
      //move driver car to chosen lane
      //move truck to partner lane

      //for cooperate == false
      //just move driver to chosen lane
      {
        choices: "NO_KEYS",
        on_load: () => {
          let laneCodes;
          if (newGame.cooperating === 1) {
            // Get the last 2 trials
            const lastTrialData = jsPsych.data.get().last(3).values();
            laneCodes = [lastTrialData[2].userKeyCode, lastTrialData[0].partnerKeyCode];
          } else {
            // Get the last trial
            const lastTrialData = jsPsych.data.get().last(1).values();
            const userKeyCode = lastTrialData[0].userKeyCode;
            laneCodes = [userKeyCode];
          }
          newGame.startPhase0_5M5(laneCodes);
        },
        on_finish: (data) => {
          data.name = "movingIntoLanes";
          data.moveTruckLane = newGame.moveTruckLane;
          SCORE.assignScore(data);
        },
      },

      {
        choices: "NO_KEYS",
        on_load: () => {
          let laneCodes;
          if (newGame.cooperating === 1) {
            // Get the last 2 trials
            const lastTrialData = jsPsych.data.get().last(4).values();
            laneCodes = [lastTrialData[2].userKeyCode, newGame.moveTruckLane];
          } else {
            // Get the last trial
            const lastTrialData = jsPsych.data.get().last(2).values();
            const userKeyCode = lastTrialData[0].userKeyCode;
            laneCodes = [userKeyCode];
          }
          newGame.startPhase0_6M5(laneCodes);
        },
        on_finish: (data) => {
          SCORE.assignScore(data);
        },
      },
      // Phase 7: Driver car travels back to the center of the highway
      // Triggered once user completes survey
      {
        choices: "NO_KEYS",
        on_load: () => newGame.startPhase7(),
        on_finish: (data) => {
          data.name = "driverAtCenter";
          data.driverToCenter = {
            timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
            driverSpeed: newGame.driverCarSpeed,
          };
          data.driverSpeed = newGame.driverCarSpeed;
          SCORE.assignScore(data);
        },
      },
    ],
  };
}

/**
 * Builds the trial needed to display the headquarter message
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} metadata The global metadata of the experiment
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} stimulus The HTML stimulus to show
 * @param {Score} score The user's current score
 * @returns
 */
// TODO: Can this be pulled into its file? Is it used in other missions?
function buildMessageTrial(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);
  const hqConfig = getHQMessagePanelConfig(MISSION_NUMBER, trialConfig.agentFile);
  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    choices: ["Space"],
    on_load: () => {
      newGame.startPhase6();
      if (hqConfig) {
        if (hqConfig.variant === "laneChoice") {
          injectHQLaneChoiceMessage(jsPsych, hqConfig);
        } else {
          injectHQMessage(jsPsych, hqConfig);
        }
      }
    },
    on_finish: (data) => {
      removeHQMessage();
      removeHQLaneChoiceMessage();
      SCORE.assignScore(data);
    },
  };
}

/**
 * Builds the trial needed for mission one's feedback
 * @param {JsPsych} jsPsych jsPsych instance being used to run the task
 * @param {Object} trialConfig The task configuration used to build the trial
 * @param {String} missionMinScore Per mission minimum score to continue
 * @param {String} missionMaxCrash Per mission max crashes allowed
 * @returns
 */
function buildMissionFiveFeedbackTask(jsPsych, trialConfig, missionMinScore, missionMaxCrash) {
  return {
    data: { trialConfig },
    timeline: [
      // Display the total rewards
      ...buildFeedbackTrial(
        jsPsych,
        SCORE,
        missionMinScore,
        missionMaxCrash,
        "assets/images/postgame/FeedbackScreen_Mission5.svg"
      ),
      // Display the user's achievements
      buildAchievementsTrial("assets/images/postgame/achievementScreen_mission5.svg"),
    ],
  };
}

function buildMissionFiveSurvey(jsPsych, metadata, trialConfig, stimulus) {
  const newGame = new Game(jsPsych, metadata, SCORE, trialConfig);

  return {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus,
    data: { trialConfig },
    timeline: [
      {
        choices: "NO_KEYS",
        trial_duration: newGame.targetLaunchDelay,
        on_load: () => newGame.startPhase0(),
      },
      {
        on_load: () => {
          newGame.startPhase(6);
          hideElement(newGame.targetCar);
          hideElement(newGame.agentImage);
          newGame.playEffect("message");
          injectSurveyPanel(jsPsych, {
            portrait: "hq-happy",
            promptText: "How are you?",
          });
        },
        choices: ["KeyS", "KeyD", "KeyF", "KeyK", "KeyL", "Semicolon"],
        trial_duration: newGame.headquarterMessageDuration,
        on_finish: (data) => {
          removeSurveyPanel();
          data.name = "messageFromHQ";
        },
      },
    ],
  };
}
